/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/naming-convention */
import { ContentBlock } from './ContentBlock.class';
import { genRanHex } from './genRanHex';
import {
  AssetType,
  EntityRangeType,
  EntityType,
  IContentBlock,
  ImageEntityType,
  InlineStyleRangeType,
  LinkEntityType,
  MarkDefsType,
  ParserConstructoProps,
  RawContentType,
} from './TextEditor.types';

export class Parser {
  children: Array<IContentBlock> = [];

  rawContentBlock: RawContentType = null;

  inlineStyleRanges: Array<InlineStyleRangeType> = null;

  currentInlineStyle: InlineStyleRangeType = null;

  blocksInRange: Array<IContentBlock> = null;

  entityRanges: Array<EntityRangeType> = null;

  currentEntityRange: EntityRangeType = null;

  markDefs: Array<MarkDefsType> = [];

  entityMap: { [key: number]: EntityType } = null;

  asset?: AssetType = null;

  _key: string = null;

  _type: string = null;

  constructor({
    entityMap,
    rawContentBlock,
    inlineStyleRanges,
    entityRanges,
  }: ParserConstructoProps) {
    this.rawContentBlock = rawContentBlock;
    this.inlineStyleRanges = inlineStyleRanges;
    this.entityRanges = entityRanges;
    this.entityMap = entityMap;
    this._key = genRanHex(13);
    this._type = rawContentBlock.type === 'atomic' ? 'image' : 'block';
  }

  private getBlocksInRange(
    offset: number,
    length: number,
  ): Array<IContentBlock> {
    const blocksInRange = this.children?.filter((mappedBlock) => {
      const { offset: startsAt, length: mappedLength, text } = mappedBlock;
      const endsAt = startsAt + mappedLength;

      const blockStarsBeforeStyleInit = startsAt <= offset;
      const blockStartsInStyle =
        startsAt >= offset && startsAt < offset + length;
      const blockEndsAfterStyleInit = endsAt > offset;
      return (
        (blockStarsBeforeStyleInit || blockStartsInStyle) &&
        blockEndsAfterStyleInit
      );
    });
    return blocksInRange;
  }

  private getBlocksInStyleRange(): void {
    const { offset, length } = this.currentInlineStyle;
    const blocksInRange = this.getBlocksInRange(offset, length);
    this.blocksInRange = blocksInRange;
  }

  private parseBlocksInStyleRange(): void {
    const { offset, length } = this.currentInlineStyle;
    this.blocksInRange.forEach((block) => {
      const { blocks, remainingStyle } = block.splitBlock(
        offset,
        length,
        this.currentInlineStyle.style,
      );
      if (!blocks) return;
      const currentLocation = this.children.indexOf(block);
      this.children.splice(currentLocation, 1, ...blocks);

      if (remainingStyle) {
        const text = this.rawContentBlock.text.slice(
          remainingStyle.offset,
          remainingStyle.length + remainingStyle.offset,
        );
        const blockContent = new ContentBlock({
          text,
          offset: remainingStyle.offset,
          length: remainingStyle.length,
          inlineStyles: [remainingStyle.style],
        });
        this.children.push(blockContent);
      }
    });
  }

  private parseIfNoBlocksInRange(): void {
    const { offset, length, style } = this.currentInlineStyle;

    const styleEndsAt = offset + length;

    if (
      !this.blocksInRange?.length &&
      styleEndsAt <= this.rawContentBlock.text.length
    ) {
      const text = this.rawContentBlock.text.slice(offset, offset + length);
      const block = new ContentBlock({
        text,
        offset,
        length,
        inlineStyles: [style],
      });
      this.children.push(block);
    }
  }

  private parseFirstBlock(): void {
    const { offset, length, style } = this.currentInlineStyle || {};
    if (!this.children?.length) {
      if (offset > 0 || typeof offset !== 'number') {
        const text = this.rawContentBlock.text.slice(0, offset);
        const newLength = offset || text.length;
        const newOffset = 0;
        this.children.push(
          new ContentBlock({
            text,
            offset: newOffset,
            length: newLength,
            inlineStyles: [],
          }),
        );
        if (!offset) {
          this.parseSainityBlock();
          return;
        }
      }

      const text = this.rawContentBlock.text.slice(offset, offset + length);
      const block = new ContentBlock({
        text,
        offset,
        length,
        inlineStyles: [style],
      });
      this.children.push(block);

      this.parseSainityBlock();
    }
  }

  private fillGap(): void {
    const lastMappedContent = this.children[this.children.length - 1];
    const lastContentEndsAt =
      lastMappedContent.offset + lastMappedContent.length;

    const { offset } = this.currentInlineStyle;

    if (lastContentEndsAt < offset) {
      const text = this.rawContentBlock.text.slice(lastContentEndsAt, offset);
      const block = new ContentBlock({
        text,
        offset: lastContentEndsAt,
        length: text.length,
        inlineStyles: [],
      });
      this.children.push(block);
    }
  }

  private checkTrailUnstyledBlock(): void {
    const { length: finalLength, offset: finalOffset } =
      this.children?.[this.children.length - 1] || {};

    if (!finalLength && finalOffset) return;

    const endsAt = finalOffset + finalLength;
    if (this.rawContentBlock?.text?.length > endsAt) {
      const text = this.rawContentBlock.text.slice(endsAt);
      const block = new ContentBlock({
        text,
        offset: endsAt,
        length: text?.length,
        inlineStyles: [],
      });
      this.children.push(block);
    }
  }

  parseSainityBlock(): void {
    if (!this.rawContentBlock.text) {
      return;
    }
    if (!this.inlineStyleRanges?.length && this.children?.length) {
      this.checkTrailUnstyledBlock();
      this.addEntitiesToBlock();
      return;
    }

    // eslint-disable-next-line max-len
    this.currentInlineStyle = this.inlineStyleRanges.shift() as InlineStyleRangeType;

    this.parseFirstBlock();
    if (!this.currentInlineStyle) {
      return;
    }

    this.fillGap();
    this.getBlocksInStyleRange();
    this.parseIfNoBlocksInRange();
    this.parseBlocksInStyleRange();

    if (this.inlineStyleRanges.length) {
      this.parseSainityBlock();
    }

    this.checkTrailUnstyledBlock();
    this.addEntitiesToBlock();
  }

  getBlocksInEntityRange(): void {
    const { length, offset } = this.currentEntityRange;
    this.blocksInRange = this.getBlocksInRange(offset, length);
  }

  addEntitiesToBlock(): void {
    if (!this.markDefs?.length) this.markDefs = [];

    this.entityRanges.forEach((entityRange) => {
      this.currentEntityRange = entityRange;
      const { key, length, offset } = entityRange;
      this.getBlocksInEntityRange();
      if (this.blocksInRange?.length) {
        this.blocksInRange.forEach((block) => {
          const { blocks } = block.splitBlock(offset, length, null) || {};
          if (blocks?.length > 1) {
            const currentLocation = this.children.indexOf(block);
            this.children.splice(currentLocation, 1, ...blocks);
          }
        });
        this.getBlocksInEntityRange();
      }
      const entity = this.entityMap[key];

      if (entity.type === 'LINK') {
        const { data, type } = (entity as unknown) as LinkEntityType;
        const marks = this.markDefs?.filter(({ href }) => href === data.url);
        if (!marks.length) {
          const _key = genRanHex(13);
          const _type = type.toLocaleLowerCase();
          const mark = { _key, _type, href: data.url };
          this.markDefs.push(mark);
          marks.push(mark);
        }
        this.blocksInRange.forEach((blockInRange) => {
          if (!blockInRange.marks?.length) {
            blockInRange.marks = [];
          }
          blockInRange.marks = [
            ...blockInRange.marks,
            ...marks.map(({ _key }) => _key),
          ];
        });
      }

      if (entity.type === 'IMAGE') {
        const { data } = (entity as unknown) as ImageEntityType;
        delete this.children;
        this.asset = {
          url: data.src,
          metadata: {
            dimensions: {
              height: data.height,
              width: data.width,
            },
          },
        };
      }
    });
  }
}
